import { ChevronUp, ChevronDown, Sparkles } from 'lucide-react';
import { Badge, getCuisineBadgeVariant } from '../common/Badge';
import { LeadScoreBadge } from '../automation/LeadScoreBadge';
import { SLAStatusIndicator, SLATimeIndicator } from '../automation/SLAStatusIndicator';
import type { Lead } from '../../api/client';
import clsx from 'clsx';

interface LeadTableProps {
  leads: Lead[];
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  onLeadClick?: (lead: Lead) => void;
}

const columns = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'score', label: 'Score', sortable: true },
  { key: 'region', label: 'Region', sortable: true },
  { key: 'cuisine_detected', label: 'Cuisine', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'sla', label: 'SLA', sortable: false },
  { key: 'first_seen_at', label: 'First Seen', sortable: true },
  { key: 'source_name', label: 'Source', sortable: false },
];

export function LeadTable({
  leads,
  sortField,
  sortOrder,
  onSort,
  onLeadClick,
}: LeadTableProps) {
  const handleSort = (key: string) => {
    onSort?.(key);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-card-border">
            {columns.map((col) => (
              <th
                key={col.key}
                className={clsx(
                  'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-font-secondary',
                  col.sortable && 'cursor-pointer hover:text-brand-primary'
                )}
                onClick={() => col.sortable && handleSort(col.key)}
              >
                <div className="flex items-center space-x-1">
                  <span>{col.label}</span>
                  {col.sortable && sortField === col.key && (
                    sortOrder === 'asc' ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-card-border">
          {leads.map((lead) => (
            <tr
              key={lead.id}
              onClick={() => onLeadClick?.(lead)}
              className={clsx(
                'hover:bg-brand-sand cursor-pointer transition-colors',
                lead.is_new && 'bg-brand-secondary bg-opacity-10'
              )}
            >
              <td className="px-4 py-3">
                <div className="flex items-center space-x-2">
                  {lead.is_new && (
                    <Sparkles className="w-4 h-4 text-brand-secondary flex-shrink-0" />
                  )}
                  <span className="font-medium text-font-primary">{lead.name}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <LeadScoreBadge score={lead.score} size="sm" />
              </td>
              <td className="px-4 py-3 text-font-secondary">{lead.region}</td>
              <td className="px-4 py-3">
                {lead.cuisine_detected ? (
                  <Badge variant={getCuisineBadgeVariant(lead.cuisine_detected)}>
                    {lead.cuisine_detected}
                  </Badge>
                ) : (
                  <span className="text-font-secondary">-</span>
                )}
              </td>
              <td className="px-4 py-3">
                <Badge variant="status" statusType={lead.status}>
                  {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                </Badge>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <SLAStatusIndicator lead={lead} size="sm" />
                  <SLATimeIndicator lead={lead} />
                </div>
              </td>
              <td className="px-4 py-3 text-font-secondary">
                {formatDate(lead.first_seen_at)}
              </td>
              <td className="px-4 py-3 text-font-secondary text-sm">
                {lead.source_name || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {leads.length === 0 && (
        <div className="text-center py-12 text-font-secondary">
          No leads found matching your filters.
        </div>
      )}
    </div>
  );
}
